import { useState } from "react";
import { toast } from "sonner";
import { KeyRound, Eye, EyeOff } from "lucide-react";
import { api, errMsg, tokens } from "@/lib/api";

const field = "mt-2 w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900";

/** Lets the operator change the admin email/password without editing platform secrets. */
export const AdminAccountCard = ({ account, onSaved }) => {
  const [email, setEmail] = useState(account?.email || "");
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [reveal, setReveal] = useState(false);
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      const r = await api.put("/admin/account", {
        current_password: current, email, new_password: next || null,
      }, true);
      setCurrent(""); setNext("");
      onSaved?.(r);
      if (r.password_changed) {
        toast.success("Password changed. Signing you in again...");
        tokens.admin.clear();
        setTimeout(() => window.location.reload(), 1200);
      } else {
        toast.success("Admin email updated");
      }
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <div className="rounded-md border border-slate-200 bg-white p-5" data-testid="admin-account-card">
      <h3 className="flex items-center gap-2 text-base font-medium text-slate-900">
        <KeyRound className="h-4 w-4" /> Admin login
      </h3>
      <p className="mt-1 text-xs text-slate-500">
        Stored in this environment's database, so preview and production are changed independently
        and no redeploy is needed. Changing the password signs out every admin session.
      </p>
      <div className="mt-4 grid gap-4 sm:grid-cols-3">
        <label className="block">
          <span className="label-caps">Email</span>
          <input data-testid="account-email" value={email} onChange={(e) => setEmail(e.target.value)} className={field} />
        </label>
        <label className="block">
          <span className="label-caps">Current password</span>
          <input data-testid="account-current-password" type={reveal ? "text" : "password"} value={current}
            onChange={(e) => setCurrent(e.target.value)} className={field} />
        </label>
        <label className="block">
          <span className="label-caps">New password (optional)</span>
          <div className="relative">
            <input data-testid="account-new-password" type={reveal ? "text" : "password"} value={next}
              onChange={(e) => setNext(e.target.value)} placeholder="min 8 characters" className={`${field} pr-10`} />
            <button type="button" data-testid="account-password-reveal" onClick={() => setReveal(!reveal)}
              aria-label={reveal ? "Hide passwords" : "Show passwords"}
              className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700">
              {reveal ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
        </label>
      </div>
      <div className="mt-4 flex flex-wrap items-center gap-3">
        <button data-testid="account-save" disabled={busy || !current}
          onClick={save}
          className="rounded-md bg-slate-900 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50">
          Save login details
        </button>
        <span className="text-xs text-slate-500">
          Last signed in: {account?.last_login_at ? new Date(account.last_login_at).toLocaleString() : "-"}
        </span>
      </div>
    </div>
  );
};
